import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import axios from 'axios';
import { selectSelectedComponent, updateComponent } from '../../../redux/editorSlice';
import { usePropertyPanel } from '../../propertyPanel/PropertyPanelContext';
import { useTranslation } from '../../../redux/TranslationHelper';
import { ImageItem } from './ImageSlider.types';

// 토글 버튼 컴포넌트 Props 인터페이스
interface ToggleButtonProps {
  label: string;
  checked: boolean;
  onChange: (value: boolean) => void;
  fullWidth?: boolean;
}

// 토글 버튼 컴포넌트
const ToggleButton: React.FC<ToggleButtonProps> = ({ label, checked, onChange, fullWidth = false }) => {
  return (
    <div className="property-row" style={{ marginBottom: '10px' }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        width: '100%'
      }}>
        <span style={{ minWidth: fullWidth ? '50%' : '30%' }}>{label}</span>
        <div 
          style={{ 
            position: 'relative', 
            width: '44px', 
            height: '22px', 
            backgroundColor: checked ? '#3b82f6' : '#ccc',
            borderRadius: '34px', 
            transition: '.3s',
            cursor: 'pointer'
          }}
          onClick={() => onChange(!checked)}
        >
          <div 
            style={{ 
              position: 'absolute',
              height: '18px',
              width: '18px',
              left: checked ? '24px' : '2px',
              bottom: '2px',
              backgroundColor: 'white',
              transition: '.3s',
              borderRadius: '50%'
            }}
          />
        </div>
      </div>
    </div>
  );
};

const ImageSliderProperties: React.FC = () => {
  const selectedComponent = useSelector(selectSelectedComponent);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  
  // PropertyPanel 컨텍스트에서 공통 함수 및 상태 가져오기
  const context = usePropertyPanel() || {};

  // 현재 속성 값 가져오기
  const getComponentProp = (key: string, defaultValue: any): any => {
    return selectedComponent?.componentProps?.[key] ?? defaultValue;
  };

  // 이미지 배열 가져오기
  const getImages = (): ImageItem[] => {
    return selectedComponent?.componentProps?.images || [];
  };

  // 컴포넌트 속성 변경 핸들러
  const handleComponentPropsChange = (property: string, value: any): void => {
    if (!selectedComponent) return;
    
    const existingProps = JSON.parse(JSON.stringify(selectedComponent.componentProps || {}));
    existingProps[property] = value;
    
    dispatch(updateComponent({
      id: selectedComponent.id,
      changes: {
        componentProps: existingProps
      }
    }));
  };

  // 이미지 추가 핸들러
  const handleAddImage = (): void => {
    const currentImages = getImages();
    const newImage: ImageItem = {
      id: Date.now().toString(),
      url: 'https://via.placeholder.com/800x400/cccccc/666666?text=New+Image',
      title: `이미지 ${currentImages.length + 1}`,
      alt: `이미지 ${currentImages.length + 1}`,
      link: '',
      openInNewTab: false
    };
    
    handleComponentPropsChange('images', [...currentImages, newImage]);
  };

  // 이미지 삭제 핸들러
  const handleRemoveImage = (imageId: string): void => {
    const currentImages = getImages();
    const updatedImages = currentImages.filter((img: ImageItem) => img.id !== imageId);
    handleComponentPropsChange('images', updatedImages);
  };

  // 이미지 속성 변경 핸들러
  const handleImageChange = (imageId: string, property: keyof ImageItem, value: any): void => {
    const currentImages = getImages();
    const updatedImages = currentImages.map((img: ImageItem) => 
      img.id === imageId ? { ...img, [property]: value } : img
    );
    handleComponentPropsChange('images', updatedImages);
  };

  // 이미지 순서 변경 핸들러
  const handleMoveImage = (imageId: string, direction: 'up' | 'down'): void => {
    const currentImages = getImages();
    const currentIndex = currentImages.findIndex((img: ImageItem) => img.id === imageId);
    
    if (currentIndex === -1) return;
    
    let newIndex: number;
    if (direction === 'up' && currentIndex > 0) {
      newIndex = currentIndex - 1;
    } else if (direction === 'down' && currentIndex < currentImages.length - 1) {
      newIndex = currentIndex + 1;
    } else {
      return;
    }
    
    const updatedImages = [...currentImages];
    [updatedImages[currentIndex], updatedImages[newIndex]] = [updatedImages[newIndex], updatedImages[currentIndex]];
    
    handleComponentPropsChange('images', updatedImages);
  };

  // 이미지 업로드 핸들러
  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>, imageId: string): Promise<void> => {
    const file = e.target.files?.[0];
    if (!file) return;

    // 이미지 파일 타입 체크
    if (!file.type.startsWith('image/')) {
      alert('이미지 파일만 업로드 가능합니다.');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('bucket', 'withcookie-bucket');
      formData.append('path', 'c/default');

      const res = await axios.post(
        `https://refrigerator.logipasta.com/v1/file`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Access-Control-Allow-Origin': '*',
          },
          timeout: 30000,
        }
      );

      if (res.data && (res.data as any).file) {
        handleImageChange(imageId, 'url', (res.data as any).file);
        alert('이미지가 성공적으로 업로드되었습니다.');
      } else {
        console.error('이미지 URL이 응답에 없습니다:', res.data);
        alert('서버 응답에 이미지 URL이 포함되어 있지 않습니다. 관리자에게 문의하세요.');
      }
    } catch (err: any) {
      console.error('이미지 업로드 실패:', err);
      
      let errorMessage = '이미지 업로드에 실패했습니다. ';
      
      if (err.response) {
        console.error('서버 응답 오류:', err.response.status, err.response.data);
        errorMessage += `서버 응답 오류 (${err.response.status}): ${JSON.stringify(err.response.data)}`;
      } else if (err.request) {
        console.error('서버 응답 없음:', err.request);
        errorMessage += '서버로부터 응답이 없습니다. 네트워크 연결을 확인해주세요.';
      } else if (err.code === 'ECONNABORTED') {
        errorMessage += '요청 시간이 초과되었습니다. 나중에 다시 시도해주세요.';
      } else {
        console.error('요청 오류:', err.message);
        errorMessage += err.message || '알 수 없는 오류가 발생했습니다.';
      }
      
      alert(errorMessage);
    }
  };

  return (
    <>
      <div className="property-group">
        <div className="property-group-title">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M21 19V5C21 3.9 20.1 3 19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19ZM8.5 13.5L11 16.51L14.5 12L19 18H5L8.5 13.5Z" fill="currentColor"/>
          </svg>
          기본 설정
        </div>

        <div className="property-row">
          <label style={{ minWidth: '30%' }}>슬라이더 제목</label>
          <input 
            type="text" 
            value={getComponentProp('sliderTitle', '이미지 슬라이더')}
            onChange={(e) => handleComponentPropsChange('sliderTitle', e.target.value)}
            style={{ width: '100%' }}
            placeholder="슬라이더 제목을 입력하세요"
          />
        </div>

        <ToggleButton
          label="제목 표시"
          checked={getComponentProp('showTitle', false)}
          onChange={(value) => handleComponentPropsChange('showTitle', value)}
        />

        <div className="property-row">
          <label style={{ minWidth: '30%' }}>슬라이더 높이</label>
          <input 
            type="text" 
            value={getComponentProp('sliderHeight', '400px')}
            onChange={(e) => handleComponentPropsChange('sliderHeight', e.target.value)}
            style={{ width: '100%' }}
            placeholder="예: 400px, 50vh"
          />
        </div>

        <div className="property-row">
          <label style={{ minWidth: '30%' }}>이미지 맞춤</label>
          <select 
            value={getComponentProp('imageObjectFit', 'cover')}
            onChange={(e) => handleComponentPropsChange('imageObjectFit', e.target.value)}
            style={{ width: '100%' }}
          >
            <option value="cover">채우기 (cover)</option>
            <option value="contain">맞춤 (contain)</option>
            <option value="fill">늘이기 (fill)</option>
            <option value="none">원본크기 (none)</option>
            <option value="scale-down">축소만 (scale-down)</option>
          </select>
        </div>

        <div className="property-row">
          <label style={{ minWidth: '30%' }}>화면에 보여줄 이미지 수</label>
          <input 
            type="number" 
            value={getComponentProp('slidesToShow', 1)}
            onChange={(e) => handleComponentPropsChange('slidesToShow', Number(e.target.value))}
            style={{ width: '100%' }}
            min="1"
            max="5"
            step="1"
          />
          <small style={{ fontSize: '11px', color: '#666', marginTop: '4px', display: 'block' }}>
            한 화면에 동시에 보여줄 이미지 개수 (1-5)
          </small>
        </div>
      </div>

      <div className="property-group">
        <div className="property-group-title">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2L13.09 8.26L22 9L13.09 9.74L12 22L10.91 9.74L2 9L10.91 8.26L12 2Z" fill="currentColor"/>
          </svg>
          슬라이더 동작
        </div>

        <ToggleButton
          label="자동 슬라이드"
          checked={getComponentProp('autoSlide', true)}
          onChange={(value) => handleComponentPropsChange('autoSlide', value)}
        />

        <div className="property-row">
          <label style={{ minWidth: '30%' }}>슬라이드 간격 (ms)</label>
          <input 
            type="number" 
            value={getComponentProp('autoSlideInterval', 4000)}
            onChange={(e) => handleComponentPropsChange('autoSlideInterval', Number(e.target.value))}
            style={{ width: '100%' }}
            min="1000"
            step="500"
          />
        </div>

        <ToggleButton
          label="무한 슬라이드"
          checked={getComponentProp('infiniteSlide', true)}
          onChange={(value) => handleComponentPropsChange('infiniteSlide', value)}
        />

        <ToggleButton
          label="네비게이션 표시"
          checked={getComponentProp('showNavigation', true)}
          onChange={(value) => handleComponentPropsChange('showNavigation', value)}
        />

        <ToggleButton
          label="인디케이터 표시"
          checked={getComponentProp('showIndicators', true)}
          onChange={(value) => handleComponentPropsChange('showIndicators', value)}
        />
      </div>

      <div className="property-group">
        <div className="property-group-title">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M21 19V5C21 3.9 20.1 3 19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19ZM8.5 13.5L11 16.51L14.5 12L19 18H5L8.5 13.5Z" fill="currentColor"/>
          </svg>
          이미지 관리
          <button
            onClick={handleAddImage}
            style={{
              marginLeft: 'auto',
              padding: '4px 8px',
              backgroundColor: '#4285f4',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px'
            }}
          >
            이미지 추가
          </button>
        </div>

        {getImages().length === 0 ? (
          <div style={{ 
            textAlign: 'center', 
            color: '#666', 
            fontStyle: 'italic',
            padding: '20px',
            border: '2px dashed #ddd',
            borderRadius: '4px'
          }}>
            이미지가 없습니다. 이미지를 추가해주세요.
          </div>
        ) : (
          getImages().map((image, index) => (
            <div key={image.id} style={{ 
              border: '1px solid #ddd', 
              borderRadius: '4px', 
              padding: '12px', 
              marginBottom: '12px',
              backgroundColor: '#f9f9f9'
            }}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: '8px'
              }}>
                <span style={{ fontWeight: '600', fontSize: '14px' }}>
                  이미지 {index + 1}
                </span>
                <div style={{ display: 'flex', gap: '4px' }}>
                  <button
                    onClick={() => handleMoveImage(image.id, 'up')}
                    disabled={index === 0}
                    style={{
                      padding: '2px 6px',
                      backgroundColor: index === 0 ? '#ccc' : '#6b7280',
                      color: 'white',
                      border: 'none',
                      borderRadius: '2px',
                      cursor: index === 0 ? 'not-allowed' : 'pointer',
                      fontSize: '10px'
                    }}
                  >
                    ↑
                  </button>
                  <button
                    onClick={() => handleMoveImage(image.id, 'down')}
                    disabled={index === getImages().length - 1}
                    style={{
                      padding: '2px 6px',
                      backgroundColor: index === getImages().length - 1 ? '#ccc' : '#6b7280',
                      color: 'white',
                      border: 'none',
                      borderRadius: '2px',
                      cursor: index === getImages().length - 1 ? 'not-allowed' : 'pointer',
                      fontSize: '10px'
                    }}
                  >
                    ↓
                  </button>
                  <button
                    onClick={() => handleRemoveImage(image.id)}
                    style={{
                      padding: '2px 6px',
                      backgroundColor: '#ef4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '2px',
                      cursor: 'pointer',
                      fontSize: '10px'
                    }}
                  >
                    삭제
                  </button>
                </div>
              </div>

              <div className="property-row" style={{ marginBottom: '8px' }}>
                <label style={{ minWidth: '25%', fontSize: '12px' }}>이미지 URL</label>
                <div style={{ display: 'flex', gap: '4px', width: '100%' }}>
                  <input 
                    type="text" 
                    value={image.url || ''}
                    onChange={(e) => handleImageChange(image.id, 'url', e.target.value)}
                    style={{ flex: 1, fontSize: '12px' }}
                    placeholder="이미지 URL 또는 업로드 버튼 사용"
                  />
                  <label style={{
                    padding: '6px 10px',
                    backgroundColor: '#4285f4',
                    color: 'white',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '11px',
                    whiteSpace: 'nowrap'
                  }}>
                    업로드
                    <input 
                      type="file" 
                      accept="image/*" 
                      onChange={(e) => handleImageUpload(e, image.id)}
                      style={{ display: 'none' }}
                    />
                  </label>
                </div>
              </div>

              <div className="property-row" style={{ marginBottom: '8px' }}>
                <label style={{ minWidth: '25%', fontSize: '12px' }}>제목</label>
                <input 
                  type="text" 
                  value={image.title || ''}
                  onChange={(e) => handleImageChange(image.id, 'title', e.target.value)}
                  style={{ width: '100%', fontSize: '12px' }}
                  placeholder="이미지 제목 (선택사항)"
                />
              </div>

              <div className="property-row" style={{ marginBottom: '8px' }}>
                <label style={{ minWidth: '25%', fontSize: '12px' }}>대체 텍스트</label>
                <input 
                  type="text" 
                  value={image.alt || ''}
                  onChange={(e) => handleImageChange(image.id, 'alt', e.target.value)}
                  style={{ width: '100%', fontSize: '12px' }}
                  placeholder="접근성을 위한 대체 텍스트"
                />
              </div>

              <div className="property-row" style={{ marginBottom: '8px' }}>
                <label style={{ minWidth: '25%', fontSize: '12px' }}>링크 URL</label>
                <input 
                  type="text" 
                  value={image.link || ''}
                  onChange={(e) => handleImageChange(image.id, 'link', e.target.value)}
                  style={{ width: '100%', fontSize: '12px' }}
                  placeholder="클릭 시 이동할 URL (선택사항)"
                />
              </div>

              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                fontSize: '12px',
                marginTop: '8px'
              }}>
                <input 
                  type="checkbox" 
                  checked={image.openInNewTab || false}
                  onChange={(e) => handleImageChange(image.id, 'openInNewTab', e.target.checked)}
                  style={{ marginRight: '6px' }}
                />
                <label>새 창에서 열기</label>
              </div>

              {image.url && (
                <div style={{ marginTop: '8px' }}>
                  <img 
                    src={image.url} 
                    alt={image.alt || image.title || '미리보기'}
                    style={{ 
                      width: '100%', 
                      maxHeight: '80px', 
                      objectFit: 'cover',
                      borderRadius: '4px',
                      border: '1px solid #ddd'
                    }}
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                </div>
              )}
            </div>
          ))
        )}
      </div>

      <div className="property-group">
        <div className="property-group-title">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2ZM21 9V7L15 1H5C3.89 1 3 1.89 3 3V19C3 20.1 3.9 21 5 21H11V19H5V3H13V9H21Z" fill="currentColor"/>
          </svg>
          도움말
        </div>

        <div style={{ fontSize: '12px', color: '#666', lineHeight: '1.4' }}>
          <p><strong>이미지 업로드 방법:</strong></p>
          <p>1. 각 이미지의 '업로드' 버튼을 클릭해서 직접 업로드하거나</p>
          <p>2. /refrigerator에서 업로드한 이미지 URL을 직접 입력하세요</p>
          
          <p style={{ marginTop: '12px' }}><strong>슬라이더 설정:</strong></p>
          <p>• 화면에 보여줄 이미지 수: 한 번에 표시할 이미지 개수 (1-5)</p>
          <p>• 1개 설정 시: 배너 스타일 (전체 화면)</p>
          <p>• 2개 이상 설정 시: 갤러리 스타일 (이미지들이 나란히 표시)</p>
          
          <p style={{ marginTop: '12px' }}><strong>슬라이더 팁:</strong></p>
          <p>• 이미지는 최소 2장 이상 권장합니다</p>
          <p>• 모든 이미지의 크기를 동일하게 맞추면 더 자연스럽습니다</p>
          <p>• 자동 슬라이드 간격은 3-5초가 적당합니다</p>
        </div>
      </div>
    </>
  );
};

export default ImageSliderProperties; 